<template>
  <div class="fill-height">
    <v-card outlined class="d-flex flex-column fill-height" elevation="0">
      <!-- Header -->
      <v-card-title class="d-flex align-center py-3 px-4" style="height: 64px">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              text
              v-bind="attrs"
              v-on="on"
              class="py-0 d-flex align-center px-2"
              style="text-transform: none; letter-spacing: normal"
            >
              {{ dataType === 'price_index' ? 'Price Index' : 'Price Changes' }}
              <v-icon right small>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="dataType = 'price_index'">
              <v-list-item-title>Price Index</v-list-item-title>
            </v-list-item>
            <v-list-item @click="dataType = 'price_changes'">
              <v-list-item-title>Price Changes</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-spacer></v-spacer>

        <!-- Date Picker -->
        <v-menu
          ref="menu"
          v-model="menu"
          :close-on-content-click="false"
          :return-value.sync="dates"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="dates.length > 0"
              text
              small
              v-bind="attrs"
              v-on="on"
              class="px-2 caption mr-1"
              style="text-transform: none"
            >
              <v-icon small left>mdi-calendar-range</v-icon>
              {{ dateRangeText }}
            </v-btn>
            <v-btn v-else icon small v-bind="attrs" v-on="on">
              <v-icon small>mdi-calendar-range</v-icon>
            </v-btn>
          </template>
          <v-date-picker v-model="dates" :max="max_date" no-title scrollable range>
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="menu = false">Cancel</v-btn>
            <v-btn text color="primary" @click="changeRange">OK</v-btn>
          </v-date-picker>
        </v-menu>

        <v-btn icon small @click="reset">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Summary Section -->
      <div
        class="px-4 py-3 lighten-5 d-flex align-center justify-space-between"
        style="height: 80px"
        v-if="!loading && hasData"
      >
        <div>
          <span class="text-h5 font-weight-bold primary--text">
            {{ displayValue }}
            <span class="text-caption" v-if="dataType == 'price_index'">%</span>
          </span>
          <span class="text-caption text--secondary ml-1" v-if="dataType == 'price_changes'"
            >products</span
          >
        </div>
        <div class="text-right">
          <div class="text-caption text--secondary font-weight-bold">
            {{ displayDate }}
          </div>
        </div>
      </div>

      <!-- <v-divider v-if="!loading && hasData"></v-divider> -->

      <!-- Content Area -->
      <v-card-text
        class="flex-grow-1 d-flex flex-column justify-center relative pa-4"
        style="min-height: 250px"
      >
        <!-- Loading -->
        <div v-if="loading" class="d-flex flex-column align-center justify-center fill-height">
          <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
          <div class="mt-3 text-caption text--secondary">Loading data...</div>
        </div>

        <!-- Empty -->
        <div v-else-if="!hasData" class="text-center my-auto">
          <v-icon size="64" color="grey lighten-2" class="mb-3">mdi-chart-line-variant</v-icon>
          <div class="text-subtitle-1 text--secondary font-weight-medium">No Data Available</div>
          <div class="text-caption text--secondary mt-1">
            Add matched products to see price index
          </div>
        </div>

        <!-- Chart -->
        <div v-else class="d-flex flex-column align-center justify-center fill-height w-100">
          <div style="position: relative; height: 200px; width: 100%">
            <canvas ref="priceindexchart" id="priceindexchart"></canvas>
            <!-- Annotation tooltip -->
            <div
              v-if="annotationTooltip.visible"
              style="
                position: absolute;
                pointer-events: none;
                background: rgba(50,50,50,0.92);
                color: #fff;
                border-radius: 6px;
                padding: 6px 10px;
                font-size: 11px;
                white-space: nowrap;
                box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                z-index: 10;
                transform: translateX(-50%);
              "
              :style="{ left: annotationTooltip.x + 'px', top: (annotationTooltip.y - 44) + 'px' }"
            >
              <div style="font-weight:600; color:#FFB74D; margin-bottom:2px">
                <v-icon x-small color="#FFB74D" style="vertical-align:middle">mdi-alert-circle-outline</v-icon>
                Price update — {{ annotationTooltip.date }}
              </div>
              <div style="color:#e0e0e0">{{ annotationTooltip.tooltip }}</div>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider v-if="!loading && hasData"></v-divider>

      <!-- Footer -->
      <v-card-actions class="px-4 py-2 lighten-5" v-if="!loading && hasData">
        <v-spacer></v-spacer>
        <div class="text-caption text--secondary">
          Updated {{ lastDate ? dateToDayName(lastDate) : '' }}
        </div>
      </v-card-actions>
    </v-card>

    <chartPriceIndexHistoryModal
      :show="chart_price_index_history_modal"
      :component_name="component_name"
    />
  </div>
</template>

<script>
  import Chart from 'chart.js'
  import axios from 'axios'
  import chartPriceIndexHistoryModal from './chartPriceIndexHistoryModal'
  import moment from 'moment'
  import annotationsConfig from '../../config/annotationsConfig.json'

  export default {
    name: 'ChartCardPriceIndex',
    props: ['component_name'],
    components: {
      chartPriceIndexHistoryModal,
    },
    data() {
      return {
        loading: false,
        dataType: 'price_index',
        menu: false,
        myChart: null,
        max_date: moment(Date.now()).format('YYYY-MM-DD'),
        dates: [],
        chart_price_index_history_modal: false,
        statistics: {
          labels: [],
          data: [],
          price_index: 0,
          changes_count: 0,
        },
        hoveredData: {
          value: '',
          label: '',
        },
        // Dates where a significant internal price change happened — shown as markers on the chart
        // Configured per account in src/config/annotationsConfig.json
        priceUpdateAnnotations: (() => {
          const accountId = localStorage.getItem('account_id')
          const accountConfig = annotationsConfig.accounts[accountId]
          return accountConfig ? accountConfig.priceUpdateAnnotations : []
        })(),
        annotationTooltip: {
          visible: false,
          x: 0,
          y: 0,
          date: '',
          tooltip: '',
        },
      }
    },
    computed: {
      hasData() {
        return this.statistics.labels && this.statistics.labels.length > 0
      },
      lastDate() {
        if (!this.hasData) return null
        return this.statistics.labels[this.statistics.labels.length - 1]
      },
      currentStatValue() {
        if (!this.hasData) return 0
        if (this.dataType === 'price_index') {
          return this.statistics.price_index
            ? parseFloat(this.statistics.price_index).toFixed(2)
            : 0
        } else {
          return this.statistics.changes_count
        }
      },
      displayValue() {
        return this.hoveredData.value !== '' ? this.hoveredData.value : this.currentStatValue
      },
      displayDate() {
        const date = this.hoveredData.label || this.lastDate
        return date ? this.fullDateToShort(date) : ''
      },
      dateRangeText() {
        if (!this.dates || this.dates.length === 0) return ''
        if (this.dates.length === 1) return this.dates[0]
        const sortedDates = [...this.dates].sort()
        return `${sortedDates[0]} ~ ${sortedDates[1]}`
      },
    },
    mounted() {
      this.initHistoryChart()
    },
    methods: {
      reset() {
        this.dates = []
        this.changeRange()
      },
      async changeRange() {
        if (this.$refs.menu) {
          this.$refs.menu.save(this.dates)
        }
        await this.initHistoryChart()
      },
      dateToDayName(date) {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        const dateParts = date.split('-')
        const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2])

        if (dateObj.toDateString() === new Date().toDateString()) return 'Today'
        if (
          dateObj.toDateString() ===
          new Date(new Date().setDate(new Date().getDate() - 1)).toDateString()
        )
          return 'Yesterday'
        return days[dateObj.getDay()]
      },
      fullDateToShort(date) {
        const months = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ]
        const dateParts = date.split('-')
        const month = months[parseInt(dateParts[1]) - 1]
        return `${month} ${dateParts[2]}`
      },
      async initHistoryChart() {
        if (localStorage.getItem('price_index_dataType'))
          this.dataType = localStorage.getItem('price_index_dataType')
        else this.dataType = 'price_index'

        this.statistics = {
          labels: [],
          data: [],
          price_index: 0,
          changes_count: 0,
        }

        const valueProperty = this.dataType === 'price_index' ? 'price_index' : 'count'

        // Fetch data
        let data = []
        try {
          data =
            this.dataType === 'price_index'
              ? await this.getStatistics()
              : await this.getPricesChangesHistory()
        } catch (e) {
          console.error(e)
        }

        const labels = []
        let datasets = [
          {
            label: '',
            data: [],
            borderSkipped: false,
            fill: false,
            borderColor: '',
            hoverBorderWidth: 1,
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 4,
          },
        ]

        const colors = data.map((value) => {
          labels.push(value.date)
          if (value[valueProperty] != null) {
            this.statistics.data.push(value[valueProperty].toFixed(2))
          }
          if (valueProperty === 'price_index') {
            return value.price_index < 0 ? '#4CAF50' : value.price_index > 0 ? '#FF5252' : '#2196F3'
          } else {
            return '#2196F3'
          }
        })

        this.statistics.labels = labels

        // Update summary stats
        if (labels.length > 0) {
          if (valueProperty === 'price_index') {
            this.statistics.price_index = this.statistics.data[this.statistics.data.length - 1]
          } else {
            this.statistics.changes_count = this.statistics.data[this.statistics.data.length - 1]
          }
        }

        datasets[0].pointBorderColor = colors.length ? colors[colors.length - 1] : '#2196F3'
        datasets[0].pointBackgroundColor = datasets[0].pointBorderColor
        datasets[0].pointHoverBackgroundColor = colors
        datasets[0].pointHoverBorderColor = colors
        datasets[0].borderColor = colors[colors.length - 1]
        datasets[0].data = this.statistics.data

        // Wait for DOM update before rendering chart
        this.$nextTick(() => {
          const ctx = this.$refs.priceindexchart
          if (!ctx) return

          if (this.myChart) {
            this.myChart.destroy()
          }

          // Add event listener for mouseleave on canvas to reset hover
          ctx.addEventListener('mouseleave', () => {
            this.hoveredData.value = ''
            this.hoveredData.label = ''
            this.annotationTooltip.visible = false
          })

          // Detect hover over annotation circle markers
          ctx.addEventListener('mousemove', (e) => {
            const rect = ctx.getBoundingClientRect()
            const mouseX = e.clientX - rect.left
            const mouseY = e.clientY - rect.top
            const meta = this.myChart && this.myChart.getDatasetMeta(0)
            const chartLabels = this.myChart && this.myChart.data.labels
            const top = this.myChart && this.myChart.chartArea && this.myChart.chartArea.top
            let hit = false
            if (meta && meta.data && chartLabels && top !== undefined) {
              this.priceUpdateAnnotations.forEach((annotation) => {
                const idx = chartLabels.indexOf(annotation.date)
                if (idx === -1) return
                const point = meta.data[idx]
                if (!point) return
                const ax = point._model.x
                const ay = top + 8
                if (Math.sqrt((mouseX - ax) ** 2 + (mouseY - ay) ** 2) <= 8) {
                  hit = true
                  ctx.style.cursor = 'pointer'
                  this.annotationTooltip = {
                    visible: true,
                    x: ax,
                    y: ay,
                    date: annotation.date,
                    tooltip: annotation.tooltip || '',
                  }
                }
              })
            }
            if (!hit) {
              ctx.style.cursor = ''
              this.annotationTooltip.visible = false
            }
          })

          // Create gradient
          const gradient = ctx.getContext('2d').createLinearGradient(0, 0, 0, 200)
          const lastColor = colors.length ? colors[colors.length - 1] : '#2196F3'

          // Convert hex to rgba for transparency
          const hexToRgba = (hex, alpha) => {
            const r = parseInt(hex.slice(1, 3), 16)
            const g = parseInt(hex.slice(3, 5), 16)
            const b = parseInt(hex.slice(5, 7), 16)
            return `rgba(${r}, ${g}, ${b}, ${alpha})`
          }

          gradient.addColorStop(0, hexToRgba(lastColor, 0.2))
          gradient.addColorStop(1, hexToRgba(lastColor, 0))

          datasets[0].backgroundColor = gradient
          datasets[0].fill = true

          this.myChart = new Chart(ctx, {
            type: 'line',
            data: {
              labels: labels,
              datasets: datasets,
            },
            options: {
              tooltips: {
                mode: 'index',
                intersect: false,
                enabled: false, // Disable default tooltip to use custom summary
                custom: (tooltipModel) => {
                  if (tooltipModel.opacity === 0) {
                    // hide
                    return
                  }
                  // Logic handled in onHover
                },
              },
              responsive: true,
              maintainAspectRatio: false,
              hover: {
                mode: 'index',
                intersect: false,
              },
              elements: {
                line: {
                  tension: 0.4,
                },
              },
              legend: {
                display: false,
              },
              layout: {
                padding: {
                  left: 4,
                  right: 4,
                  top: 10,
                  bottom: 0,
                },
              },
              scales: {
                xAxes: [
                  {
                    ticks: {
                      display: true,
                      maxRotation: 0,
                      autoSkip: false,
                      fontSize: 10,
                      callback: (value, index, values) => {
                        // Show first and last date only
                        if (index === values.length - 1) return this.fullDateToShort(value)
                        else if (index === 0) return this.fullDateToShort(value)
                        else return ''
                      },
                    },
                    gridLines: {
                      display: false,
                      drawBorder: false,
                    },
                  },
                ],
                yAxes: [
                  {
                    ticks: {
                      display: false,
                      beginAtZero: false,
                    },
                    gridLines: {
                      display: false,
                      drawBorder: false,
                    },
                  },
                ],
              },
              onHover: (event, active) => {
                if (active.length) {
                  let first = active[0]
                  let setIndex = first._datasetIndex
                  let index = first._index
                  let value = first._chart.chart.data.datasets[setIndex].data[index]
                  let label = first._chart.chart.data.labels[index]
                  this.hoveredData.value = value
                  this.hoveredData.label = label
                }
              },
            },
            plugins: [
              {
                afterDraw: (chart) => {
                  const annotations = this.priceUpdateAnnotations
                  if (!annotations || !annotations.length) return
                  const chartLabels = chart.data.labels
                  const meta = chart.getDatasetMeta(0)
                  if (!meta || !meta.data || !meta.data.length) return
                  const chartCtx = chart.ctx
                  const top = chart.chartArea.top
                  const bottom = chart.chartArea.bottom

                  annotations.forEach((annotation) => {
                    const idx = chartLabels.indexOf(annotation.date)
                    if (idx === -1) return
                    const point = meta.data[idx]
                    if (!point) return
                    const x = point._model.x
                    const color = annotation.color || 'rgba(255, 152, 0, 1)'
                    const markerLabel = annotation.label || 'P'

                    // Vertical dashed line
                    chartCtx.save()
                    chartCtx.beginPath()
                    chartCtx.strokeStyle = color.replace(/[.\d]+\)$/, '0.85)')
                    chartCtx.lineWidth = 1.5
                    chartCtx.setLineDash([4, 3])
                    chartCtx.moveTo(x, top)
                    chartCtx.lineTo(x, bottom)
                    chartCtx.stroke()

                    // Small circle marker on the line at the top
                    chartCtx.setLineDash([])
                    chartCtx.beginPath()
                    chartCtx.arc(x, top + 8, 6, 0, 2 * Math.PI)
                    chartCtx.fillStyle = color
                    chartCtx.fill()

                    // Label inside the circle
                    chartCtx.fillStyle = '#fff'
                    chartCtx.font = 'bold 8px sans-serif'
                    chartCtx.textAlign = 'center'
                    chartCtx.textBaseline = 'middle'
                    chartCtx.fillText(markerLabel, x, top + 8)

                    chartCtx.restore()
                  })
                },
              },
            ],
          })
        })
      },
      async getPricesChangesHistory() {
        let from, to, temp1, temp2

        if (this.dates.length) {
          temp1 = this.dates[0].split('-')
          let x = new Date(
            Date.UTC(temp1[0], temp1[1] - 1, temp1[2] != undefined ? temp1[2] : 1, 0, 0, 0),
          )

          temp2 = this.dates[1] == undefined ? this.dates[0].split('-') : this.dates[1].split('-')
          let y = new Date(
            Date.UTC(temp2[0], temp2[1] - 1, temp2[2] != undefined ? temp2[2] : 1, 22, 0, 0),
          )

          if (+x <= +y) {
            from = x.getTime()
            to = y.getTime()
          } else {
            y.setHours(0)
            x.setHours(22)
            from = y.getTime()
            to = x.getTime()
          }
        } else {
          to = 0
          from = 0
        }
        this.loading = true
        return await axios
          .get(`/api/products/priceschangeshistory`, {
            params: {
              account: localStorage.getItem('account_id'),
              user: localStorage.getItem('user_id'),
              component: this.component_name,
              search_filter: localStorage.getItem('filter'),
              updateInterval: localStorage.getItem('updateInterval') || 1,
              full: false,
              from: from,
              to: to,
            },
          })
          .then((res) => {
            return res.data.statisticsData
          })
          .finally(() => {
            this.loading = false
          })
      },
      async getStatistics() {
        let from, to, temp1, temp2

        if (this.dates.length) {
          temp1 = this.dates[0].split('-')
          let x = new Date(
            Date.UTC(temp1[0], temp1[1] - 1, temp1[2] != undefined ? temp1[2] : 1, 0, 0, 0),
          )

          temp2 = this.dates[1] == undefined ? this.dates[0].split('-') : this.dates[1].split('-')
          let y = new Date(
            Date.UTC(temp2[0], temp2[1] - 1, temp2[2] != undefined ? temp2[2] : 1, 22, 0, 0),
          )

          if (+x <= +y) {
            from = x.getTime()
            to = y.getTime()
          } else {
            y.setHours(0)
            x.setHours(22)
            from = y.getTime()
            to = x.getTime()
          }
        } else {
          to = 0
          from = 0
        }
        this.loading = true
        return await axios
          .get(`/api/products/statisticshistory`, {
            params: {
              account: localStorage.getItem('account_id'),
              user: localStorage.getItem('user_id'),
              component: this.component_name,
              updateInterval: localStorage.getItem('updateInterval') || 1,
              search_filter: localStorage.getItem('filter'),
              full: false,
              from: from,
              to: to,
            },
          })
          .then((res) => {
            return res.data.statisticsData
          })
          .finally(() => {
            this.loading = false
          })
      },
    },
    watch: {
      dataType: {
        handler: function (val) {
          if (localStorage.getItem('price_index_dataType') == val) return
          localStorage.setItem('price_index_dataType', val)
          this.initHistoryChart()
        },
      },
    },
  }
</script>
<style scoped>
  /* No custom styles needed as we use Vuetify classes */
</style>
